import { createSelector } from 'reselect';
import { RootState } from 'types';
import { EntityTypes } from 'constants/entities';
import { IEntityReducer } from 'core/reducers/entities';
import { IActionBasic } from 'types/domain/action';
import { parseAction } from './action';
import { IActionFeed, IActionFeedItem, selectActionFeed } from './actionFeed';

const entity = (state: RootState) => state.entity.present;
const id = (state: RootState, id?: number) => id || state.state[EntityTypes.ACTION];
const feed = selectActionFeed;

const getActionConfigEnd = (action: IActionBasic) => {
  const config = action?.voiceSceneConfigId ? action.voiceSceneConfig : action.sceneConfig;
  return config?.end;
};

const getActionConfigStart = (action: IActionBasic) => {
  const config = action?.voiceSceneConfigId ? action.voiceSceneConfig : action.sceneConfig;
  return config?.start;
};

const getActionSceneId = (action: IActionBasic) => action?.voiceSceneId ?? action?.sceneId;

/**
 * An action's effective `end` on `sceneId` — its own config's end, falling back to
 * the scene's (the same per-key merge selectSceneConfig does). `sceneId` may be the
 * action's scene or its voiceover scene. `undefined` when the action isn't on that
 * scene; `null` when it plays to the end of the video.
 */
function getMergedSceneEnd(entity: IEntityReducer, actionId: number, sceneId: number): number | null | undefined {
  const action = actionId ? entity[EntityTypes.ACTION][actionId] : null;
  if (!action) return undefined;

  const ownConfigId =
    action.voiceSceneId === sceneId
      ? action.voiceSceneConfigId
      : action.sceneId === sceneId
      ? action.sceneConfigId
      : undefined;
  if (ownConfigId === undefined) return undefined;

  const scene = entity[EntityTypes.SCENE][sceneId];
  const own = ownConfigId != null ? entity[EntityTypes.SCENE_CONFIG][ownConfigId] : null;
  const base = scene ? entity[EntityTypes.SCENE_CONFIG][scene.sceneConfigId] : null;

  return (own?.end ?? base?.end) || null;
}

export const selectActionSceneEnd = (state: RootState, actionId: number, sceneId: number) =>
  getMergedSceneEnd(state.entity.present, actionId, sceneId);

/**
 * The furthest `end` across the unbroken run of feed actions that share `sceneId`
 * with the current action (or its neighbour, while a scene loads ahead of or behind
 * the action pointer). One loaded source serves that whole run — moving to the next
 * action on the same scene only seeks — so its `#t=…,end` must cover every action
 * in it, not just the one that happened to be current when it loaded.
 *
 * `undefined` when no action near the current one is on the scene; `null` when any
 * action in the run plays to the end of the video.
 */
export function selectSceneRunEnd(state: RootState, sceneId: number): number | null | undefined {
  const entity = state.entity.present;
  const { feed, index } = selectActionFeed(state);
  const current = feed.findIndex(item => item.actionId === state.state[EntityTypes.ACTION]);
  const pivot = current === -1 ? index : current;
  const onScene = (i: number) => getMergedSceneEnd(entity, feed[i]?.actionId, sceneId) !== undefined;

  let first = [pivot, pivot - 1, pivot + 1].find(onScene);
  if (first === undefined) return undefined;

  let last = first;
  while (onScene(first - 1)) first -= 1;
  while (onScene(last + 1)) last += 1;

  let runEnd = 0;
  for (let i = first; i <= last; i += 1) {
    const end = getMergedSceneEnd(entity, feed[i].actionId, sceneId);
    if (end == null) return null;
    runEnd = Math.max(runEnd, end);
  }

  return runEnd;
}

export const selectActionEndTime = createSelector(
  [entity, id, feed],
  (entity: IEntityReducer, id: number, feed: IActionFeed) => {
    const action = parseAction(entity, id);

    if (!action) return null;

    const actionEnd = getActionConfigEnd(action);
    if (actionEnd == null) return null;

    const currentIndex = feed.feed.findIndex((item: IActionFeedItem) => item.actionId === id);
    if (currentIndex === -1) return actionEnd;

    const nextActionId = feed.feed[currentIndex + 1]?.actionId;
    if (!nextActionId) return actionEnd;

    const nextAction = parseAction(entity, nextActionId);
    if (!nextAction) return actionEnd;

    const hasSameScene = getActionSceneId(action) === getActionSceneId(nextAction);
    if (!hasSameScene) return actionEnd;

    const nextActionStart = getActionConfigStart(nextAction);

    return nextActionStart != null && nextActionStart < actionEnd ? nextActionStart : actionEnd;
  },
);
